/**
 * AuditLogService — one row per SESSION of somebody using the app.
 *
 * A self-provisioned list (iSyn_AuditLog) on the Manufacturing site, beside
 * iSyn_Presence and for the same reason: nothing else reads it, so it is
 * created by the first session that needs it and Migration Valve is not
 * involved. Presence keeps ONE overwritten row per person; this keeps history,
 * so it is append-only from the app's side — it creates a row when a session
 * starts and bumps it while the session lasts. Nothing here ever deletes.
 *
 * The times are NOT stored. A session's start is the row's `Created` and its end
 * the row's `Modified`, both stamped by SharePoint, so no laptop's clock can
 * skew a session's length (see auditSession.ts for why that matters). The only
 * columns we add are the display name and a beat counter; the counter exists so
 * every heartbeat is a real change — an update that changes nothing may not
 * touch `Modified`, and then the session would never appear to last.
 *
 * Best-effort throughout: a failed write must never get in front of somebody
 * working, so the runner swallows everything this throws.
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { isMissingListError, type IAuditRaw } from "./auditSession";

const LIST_NAME = "iSyn_AuditLog";
const SITE_URL = MFG_SITE_URL.replace(/\/$/, "");

/** Read pair, per CLAUDE.md: a select naming a column the list lacks 400s the
 * whole read, so the full select falls back to built-in columns only. */
const AUDIT_SELECT = "Id,Title,DisplayName,Created,Modified";
const AUDIT_CORE = "Id,Title,Created,Modified";

const FIELD_SCHEMAS: Record<string, string> = {
  DisplayName: `<Field Type="Text" DisplayName="DisplayName" Name="DisplayName" StaticName="DisplayName" MaxLength="200" />`,
  Beats: `<Field Type="Number" DisplayName="Beats" Name="Beats" StaticName="Beats" Decimals="0" />`,
};

export class AuditLogService {
  private readonly sp = createSharePointClient(SITE_URL);
  private entityTypeCache: string | null = null;
  private provisionedPromise: Promise<void> | null = null;

  private ensureList(): Promise<void> {
    if (!this.provisionedPromise) {
      this.provisionedPromise = this.provision().catch((err) => { this.provisionedPromise = null; throw err; });
    }
    return this.provisionedPromise;
  }

  private listPath(): string {
    return `/_api/web/lists/getbytitle('${encodeURIComponent(LIST_NAME)}')`;
  }

  private async provision(): Promise<void> {
    let exists = true;
    try { await this.sp.raw(`${this.listPath()}?$select=Id`); }
    catch { exists = false; }
    if (!exists) {
      await this.sp.raw(`/_api/web/lists`, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.List" }, Title: LIST_NAME, BaseTemplate: 100,
          Description: "Audit log — one row per session of somebody using i-SYNERGY. Start = Created, end = Modified.",
        }),
      });
    }
    let existing = new Set<string>();
    try {
      const data = await this.sp.raw<{ d: { results: { InternalName: string }[] } }>(`${this.listPath()}/fields?$select=InternalName`);
      existing = new Set(data.d.results.map((f) => f.InternalName));
    } catch { /* attempt each create */ }
    for (const [name, schemaXml] of Object.entries(FIELD_SCHEMAS)) {
      if (existing.has(name)) continue;
      try {
        await this.sp.raw(`${this.listPath()}/fields/createfieldasxml`, {
          method: "POST",
          body: JSON.stringify({ parameters: { __metadata: { type: "SP.XmlSchemaFieldCreationInformation" }, SchemaXml: schemaXml } }),
        });
      } catch { /* exists / raced */ }
    }
    // The log only grows, and the screen filters it by date. SharePoint will not
    // filter or sort an UNINDEXED column once a list passes 5,000 items — at a
    // few sessions a person a day that is weeks away, and the screen would die
    // with a threshold error. Indexing while the list is still empty is free;
    // doing it later, over 5,000 rows, is refused. Best-effort: the list works
    // without it until it is big, and the screen says what to do if it is not.
    try {
      await this.sp.raw(`${this.listPath()}/fields/getbytitle('Created')`, {
        method: "POST",
        headers: { "X-HTTP-Method": "MERGE", "IF-MATCH": "*" },
        body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
      });
    } catch { /* already indexed / not permitted */ }
  }

  private async getEntityType(): Promise<string> {
    if (this.entityTypeCache) return this.entityTypeCache;
    const data = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `${this.listPath()}?$select=ListItemEntityTypeFullName`);
    this.entityTypeCache = data.d.ListItemEntityTypeFullName;
    return this.entityTypeCache;
  }

  /** Begin a session; returns its row id so the caller can keep beating it. */
  public async startSession(email: string, name: string): Promise<number> {
    await this.ensureList();
    const entityType = await this.getEntityType();
    const created = await this.sp.createItem<{ Id: number }>(LIST_NAME, {
      __metadata: { type: entityType },
      Title: email.trim().toLowerCase(),
      DisplayName: name || email,
      Beats: 1,
    });
    return created.Id;
  }

  /** Keep a session alive. `beats` must differ from the last write — see top. */
  public async beat(rowId: number, beats: number): Promise<void> {
    const entityType = await this.getEntityType();
    await this.sp.updateItem(LIST_NAME, rowId, { __metadata: { type: entityType }, Beats: beats });
  }

  /**
   * Sessions that STARTED in [fromIso, toIso), newest first. A session that
   * began just before the range and ran into it is not in it — the date column
   * is the start date, and the range is a statement about dates.
   *
   * A list that does not exist yet is an empty log, not an error: it is created
   * by the first session anyone records.
   */
  public async listSessions(range: { fromIso: string; toIso: string }): Promise<{ rows: IAuditRaw[]; missing: boolean }> {
    const filter = `Created ge datetime'${range.fromIso}' and Created lt datetime'${range.toIso}'`;
    const read = (select: string) =>
      this.sp.getListItems<IAuditRaw>(LIST_NAME, { select, filter, orderby: "Created desc" });
    try {
      try {
        return { rows: await read(AUDIT_SELECT), missing: false };
      } catch (err) {
        const msg = String((err as Error)?.message ?? err);
        if (isMissingListError(msg)) throw err;
        // A column the list does not have yet: the same read without it. The
        // names then come from the directory alone, which is what they are for.
        return { rows: await read(AUDIT_CORE), missing: false };
      }
    } catch (err) {
      if (isMissingListError(String((err as Error)?.message ?? err))) return { rows: [], missing: true };
      throw err;
    }
  }
}

export const auditLogService = new AuditLogService();
